import React from 'react'
import classNames from 'classnames'
import { motion } from 'motion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { useRoutes } from '../../routes'
import type { ComparisonChart } from './data'
import { getMcpComparisonCharts } from './data'

const BAR_HEIGHT = 40
const BAR_RADIUS = 12

const BAR_THEMES: Record<
  ComparisonChart['bars'][number]['theme'],
  { fill: React.CSSProperties; track: React.CSSProperties }
> = {
  brand: {
    fill: { background: 'var(--saturn-orange)' },
    track: {
      background: 'var(--alpha-orange-50)',
      border: '1px solid var(--saturn-orange)',
    },
  },
  neutral: {
    fill: { background: 'var(--shade-30)' },
    track: {
      background: 'var(--alpha-shade-55)',
      border: '1px solid var(--shade-30)',
    },
  },
}

function ChartCard({ chart }: { chart: ComparisonChart }) {
  return (
    <div className="grid-span-1-large grid-span-1-medium grid-span-2-small">
      <div className="text-align-center">
        <div className="font-size-3 font-family-title font-weight-medium shade-05">
          {chart.title}
        </div>
        <p
          className={classNames(
            'font-size-6 shade-20 line-height-large margin-top-8',
            'margin-left-auto margin-right-auto'
          )}
          style={{ maxWidth: 300, height: 80 }}
        >
          {chart.subtitle}
        </p>
      </div>

      <div className="flexbox flexbox-column flexbox-gap-20">
        {chart.bars.map((bar, index) => {
          const theme = BAR_THEMES[bar.theme]
          return (
            <div
              key={bar.label}
              className="flexbox flexbox-align-center flexbox-gap-16"
            >
              <div
                className="flexbox-no-shrink text-align-right"
                style={{ width: 150 }}
              >
                <div className="font-size-7 font-weight-semibold shade-00">
                  {bar.label}
                </div>
                <div className="font-size-7 shade-20">{bar.sublabel}</div>
              </div>
              <div
                className="flexbox flexbox-grow"
                style={{
                  height: BAR_HEIGHT,
                  borderRadius: BAR_RADIUS,
                  overflow: 'hidden',
                  ...theme.track,
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${bar.fillPct}%` }}
                  viewport={{ amount: 0.5 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.08 * index,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  style={{
                    height: '100%',
                    ...theme.fill,
                  }}
                />
              </div>
              <div
                className="flexbox-no-shrink font-size-4 font-weight-bold shade-05"
                style={{ width: 76 }}
              >
                {bar.value}
                {chart.valueSuffix}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function SectionMcpComparison() {
  const intl = useIntl()
  const routes = useRoutes()

  const charts = getMcpComparisonCharts(intl)
  const videoUrl = `https://storage.googleapis.com/slite-cdn/videos/MCP-benchmark-demo.mp4`
  const benchmarkLearnSlug = 'mcp-vs-dedicated-company-search-agent'

  return (
    <Section
      containerVerticalPadding={120}
      background="moon-dust-lighter"
      className="position-relative"
    >
      <div
        className={classNames(
          'flexbox flexbox-column flexbox-align-center text-align-center',
          'margin-left-auto margin-right-auto'
        )}
        style={{ maxWidth: 800 }}
      >
        <h2
          className="font-family-title font-size-2 line-height-normal font-weight-medium shade-05"
          style={{ letterSpacing: '-0.02em' }}
        >
          <FormattedMessage
            id="mcp.comparison.title"
            defaultMessage="Purpose-built knowledge agent vs 10 MCP connectors"
          />
        </h2>
      </div>

      <div
        className={classNames(
          'margin-top-32',
          'margin-left-auto margin-right-auto'
        )}
        style={{ maxWidth: 1000 }}
      >
        <div className="width-full margin-bottom-60">
          <video
            src={videoUrl}
            autoPlay
            muted
            playsInline
            preload="metadata"
            className="width-full radius-semilarger overflow-hidden background-shade-100 transition"
            controls
          />
        </div>
        <div className="grid-2 position-relative" style={{ gap: 80 }}>
          <div
            style={{ width: 1, left: '50%', height: '100%' }}
            className="position-absolute display-none-small background-shade-45"
          />
          {charts.map((chart) => (
            <ChartCard key={chart.title} chart={chart} />
          ))}
        </div>
      </div>
      <div className="flexbox flexbox-column flexbox-centered margin-top-60">
        <Button
          variant="primary"
          size="default"
          href={routes.learn.detail({ slug: benchmarkLearnSlug })}
        >
          <FormattedMessage
            id="mcp.comparison.cta"
            defaultMessage="Explore the full benchmark"
          />
        </Button>
      </div>
    </Section>
  )
}
